import { debounce } from 'lodash';
import {
  TerraDraw,
  TerraDrawCircleMode,
  TerraDrawPolygonMode,
  TerraDrawRectangleMode,
  TerraDrawSelectMode,
} from 'terra-draw';
import { TerraDrawGoogleMapsAdapter } from 'terra-draw-google-maps-adapter';
import {
  EARTH_RADIUS_METERS,
  CIRCLE_SEGMENTS,
  DRAW_STYLES,
  FULL_EDIT_FLAGS,
  MOVE_ONLY_FLAGS,
  BUTTON_SIZE_PX,
  TOOLBAR_MODES,
  COORDINATE_PRECISION
} from 'helpers/research/map_constants';

export default class PropertiesMapDrawingManager {
  constructor(google, map, { onBoundsChange } = {}) {
    this.google = google;
    this.map = map;
    this.onBoundsChange = onBoundsChange || (() => {});
    this.currentFeatureId = null;
    this.currentMode = 'select';
    this.buttons = {};
    this.clearButton = null;
    // Guards against onBoundsChange firing while we render a shape that came from props.
    this.rendering = false;
    // TerraDraw can only start once the map has rendered (see start()). Until then, a shape supplied
    // via renderBounds is held here and applied on start.
    this.started = false;
    this.pendingFeature = null;

    this.debouncedEmitBounds = debounce(() => this.emitBounds(), 100);

    if (!map) return;

    // The TerraDraw Google Maps adapter requires the map container div to have an id. ClusteredMap
    // doesn't set one, so assign a unique id here before wiring up the adapter.
    const container = map.getDiv();
    if (container && !container.id) {
      container.id = `terra-draw-map-${uuid()}`;
    }

    this.draw = new TerraDraw({
      adapter: new TerraDrawGoogleMapsAdapter({
        lib: google.maps,
        map,
        coordinatePrecision: 9,
      }),
      modes: [
        new TerraDrawPolygonMode({ styles: { ...DRAW_STYLES, closingPointColor: '#1D357E' } }),
        new TerraDrawRectangleMode({ styles: DRAW_STYLES }),
        new TerraDrawCircleMode({ styles: DRAW_STYLES }),
        new TerraDrawSelectMode({
          flags: { polygon: FULL_EDIT_FLAGS, rectangle: MOVE_ONLY_FLAGS, circle: MOVE_ONLY_FLAGS },
        }),
      ],
    });

    this.draw.on('finish', this.handleFinish);
    this.draw.on('change', this.handleChange);

    // The adapter attaches listeners to the map's rendered panes, which don't exist until the map
    // has drawn at least once. Defer start() to the first idle event so those panes are present.
    // Keep the handle so destroy() can remove it if the component unmounts before idle fires.
    this.idleListener = google.maps.event.addListenerOnce(map, 'idle', this.start);

    this.buildToolbar();
  }

  start = () => {
    if (this.started || !this.draw) return;
    this.started = true;
    this.draw.start();
    this.draw.setMode('select');

    if (this.pendingFeature) {
      const feature = this.pendingFeature;
      this.pendingFeature = null;
      this.applyFeature(feature);
    }
  };

  // Render a shape that arrived via props (e.g. a saved search). Returns a google.maps.LatLngBounds
  // so the caller can fit the map. Does NOT fire onBoundsChange - the bounds already live in state.
  renderBounds(bounds) {
    const feature = boundsToFeature(bounds);
    if (!feature) {
      this.pendingFeature = null;
      if (this.started) {
        this.draw.clear();
        this.currentFeatureId = null;
      }
      this.showClear(false);
      return null;
    }

    feature.id = uuid();

    if (this.started) {
      this.applyFeature(feature);
    } else {
      // TerraDraw isn't running yet; render the shape on start(). The map can still be fit below.
      this.pendingFeature = feature;
    }

    const latLngBounds = new this.google.maps.LatLngBounds();
    feature.geometry.coordinates[0].forEach(([lng, lat]) => latLngBounds.extend({ lat, lng }));
    return latLngBounds;
  }

  applyFeature(feature) {
    this.rendering = true;
    this.draw.clear();

    const [validation] = this.draw.addFeatures([feature]);
    if (validation && validation.valid === false) {
      this.currentFeatureId = null;
      this.showClear(false);
      this.rendering = false;
      return;
    }

    this.currentFeatureId = feature.id;
    this.currentMode = 'select';
    this.draw.setMode('select');
    this.showClear(true);
    this.updateToolbar(null);
    this.debouncedEmitBounds.cancel();
    this.rendering = false;
  }

  deleteOverlay() {
    const hadFeature =
      this.currentFeatureId != null ||
      this.pendingFeature != null ||
      (this.started && this.draw.getSnapshot().length > 0);

    this.pendingFeature = null;
    if (this.started) {
      this.draw.clear();
      this.draw.setMode('select');
    }
    this.currentFeatureId = null;
    this.currentMode = 'select';
    this.showClear(false);
    this.updateToolbar(null);

    if (hadFeature) {
      this.onBoundsChange(null);
    }
  }

  destroy() {
    if (!this.draw) return;

    // Remove the deferred idle listener in case the component unmounts before the map ever idled -
    // otherwise start() could run against a torn-down map.
    if (this.idleListener) {
      this.google.maps.event.removeListener(this.idleListener);
      this.idleListener = null;
    }

    this.draw.off('finish', this.handleFinish);
    this.draw.off('change', this.handleChange);
    this.debouncedEmitBounds.cancel();
    this.deleteOverlay();
    this.draw.stop();
    this.started = false;
  }

  handleFinish = (id, context) => {
    if (context.action !== 'draw') return;

    this.currentFeatureId = id;
    this.currentMode = 'select';
    this.emitBounds();
    this.draw.setMode('select');
    this.showClear(true);
    this.updateToolbar(null);
  };

  handleChange = () => {
    // Only react to edits of the committed shape, not points being added mid-draw or our own
    // renderBounds() calls.
    if (this.rendering || this.currentMode !== 'select' || this.currentFeatureId == null) return;
    this.debouncedEmitBounds();
  };

  emitBounds() {
    const feature = this.draw.getSnapshot().find(f => f.id === this.currentFeatureId);
    if (feature) {
      this.onBoundsChange(featureToBounds(feature));
    }
  }

  selectMode = (mode) => {
    if (!this.started) return;

    if (mode === null) {
      this.currentMode = 'select';
      this.draw.setMode('select');
      this.updateToolbar(null);
      return;
    }

    // Starting a fresh drawing clears any existing shape (one search area at a time).
    if (this.draw.getSnapshot().length > 0) {
      this.draw.clear();
      this.currentFeatureId = null;
      this.showClear(false);
    }
    this.currentMode = mode;
    this.draw.setMode(mode);
    this.updateToolbar(mode);
  };

  buildToolbar() {
    const controls = this.map.controls[this.google.maps.ControlPosition.TOP_LEFT];

    const container = document.createElement('div');
    container.classList.add('p-1', 'bg-white', 'rounded', 'm-2', 'map-controls', 'shadow');

    const btnGroup = document.createElement('div');
    btnGroup.classList.add('btn-group', 'gap-1');

    TOOLBAR_MODES.forEach(({ mode, icon, title }) => {
      const btn = document.createElement('div');
      btn.innerHTML = `<i class="fa-regular ${icon}" />`;
      btn.title = title;
      btn.classList.add('btn', 'border-0');
      squareButton(btn);
      btn.onclick = () => this.selectMode(mode);
      this.buttons[mode === null ? 'pan' : mode] = btn;
      btnGroup.append(btn);
    });

    const clearBtn = document.createElement('div');
    clearBtn.innerHTML = '<i class="fa fas fa-times text-danger" />';
    clearBtn.title = 'Clear drawing';
    clearBtn.classList.add('btn', 'btn-light', 'bg-white', 'border-0');
    squareButton(clearBtn);
    clearBtn.style.display = 'none';
    clearBtn.onclick = () => this.deleteOverlay();
    this.clearButton = clearBtn;
    btnGroup.append(clearBtn);

    container.append(btnGroup);
    controls.push(container);

    this.updateToolbar(null);
  }

  updateToolbar(activeMode) {
    const setActive = (btn, active) => {
      btn.classList.remove('btn-shaded-primary', 'btn-light', 'bg-white');
      if (active) {
        btn.classList.add('btn-shaded-primary', 'text-primary');
      } else {
        btn.classList.add('btn-light', 'bg-white', 'text-primary');
      }
    };

    setActive(this.buttons.pan, activeMode === null);
    ['circle', 'polygon', 'rectangle'].forEach(mode => setActive(this.buttons[mode], activeMode === mode));
  }

  showClear(visible) {
    if (this.clearButton) {
      this.clearButton.style.display = visible ? 'flex' : 'none';
    }
  }
}

function squareButton(btn) {
  btn.style.width = `${BUTTON_SIZE_PX}px`;
  btn.style.height = `${BUTTON_SIZE_PX}px`;
  btn.style.padding = '0';
  btn.style.flex = '0 0 auto';
  btn.style.display = 'flex';
  btn.style.alignItems = 'center';
  btn.style.justifyContent = 'center';
}

// --- geometry helpers -------------------------------------------------------

function featureToBounds(feature) {
  const mode = feature.properties && feature.properties.mode;
  const points = ringLatLngs(feature);

  switch (mode) {
  case 'circle': {
    const center = centroid(points);
    return {
      center,
      radius: haversineMeters(center, points[0]),
      radiusUnits: 'm',
      type: 'circle',
    };
  }
  case 'rectangle': {
    const lats = points.map(p => p.lat);
    const lngs = points.map(p => p.lng);
    return {
      northEast: { lat: Math.max(...lats), lng: Math.max(...lngs) },
      southWest: { lat: Math.min(...lats), lng: Math.min(...lngs) },
      type: 'rectangle',
    };
  }
  default:
    return { points, type: 'polygon' };
  }
}

function boundsToFeature(bounds) {
  switch (bounds && bounds.type) {
  case 'circle':
    return circleFeature(bounds.center, bounds.radius);
  case 'rectangle':
    return rectangleFeature(bounds.northEast, bounds.southWest);
  case 'polygon':
    return polygonFeature(bounds.points);
  default:
    return null;
  }
}

function circleFeature(center, radiusMeters) {
  const ring = [];
  for (let i = 0; i <= CIRCLE_SEGMENTS; i += 1) {
    const bearing = (i % CIRCLE_SEGMENTS) * (360 / CIRCLE_SEGMENTS);
    const point = destinationPoint(center, radiusMeters, bearing);
    ring.push([point.lng, point.lat]);
  }
  return polygonGeoJson(ring, 'circle');
}

function rectangleFeature(northEast, southWest) {
  const ring = [
    [southWest.lng, southWest.lat],
    [northEast.lng, southWest.lat],
    [northEast.lng, northEast.lat],
    [southWest.lng, northEast.lat],
    [southWest.lng, southWest.lat],
  ];
  return polygonGeoJson(ring, 'rectangle');
}

function polygonFeature(points) {
  const ring = points.map(p => [p.lng, p.lat]);
  if (ring.length > 0) {
    ring.push(ring[0]);
  }
  return polygonGeoJson(ring, 'polygon');
}

function polygonGeoJson(ring, mode) {
  // Round to the adapter's precision or TerraDraw rejects the feature ("excessive precision").
  // First and last points round identically, so ring closure is preserved.
  const rounded = ring.map(([lng, lat]) => [round(lng), round(lat)]);
  return {
    type: 'Feature',
    geometry: { type: 'Polygon', coordinates: [rounded] },
    properties: { mode },
  };
}

// Outer ring as { lat, lng } points, dropping the duplicated closing coordinate.
function ringLatLngs(feature) {
  const ring = feature.geometry.coordinates[0];
  return ring.slice(0, ring.length - 1).map(([lng, lat]) => ({ lat, lng }));
}

function centroid(points) {
  const sum = points.reduce(
    (acc, p) => ({ lat: acc.lat + p.lat, lng: acc.lng + p.lng }),
    { lat: 0, lng: 0 }
  );
  return { lat: sum.lat / points.length, lng: sum.lng / points.length };
}

function haversineMeters(a, b) {
  const dLat = toRadians(b.lat - a.lat);
  const dLng = toRadians(b.lng - a.lng);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRadians(a.lat)) * Math.cos(toRadians(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_METERS * Math.asin(Math.sqrt(h));
}

function destinationPoint(center, distanceMeters, bearingDegrees) {
  const angular = distanceMeters / EARTH_RADIUS_METERS;
  const bearing = toRadians(bearingDegrees);
  const lat1 = toRadians(center.lat);
  const lng1 = toRadians(center.lng);

  const lat2 = Math.asin(
    Math.sin(lat1) * Math.cos(angular) + Math.cos(lat1) * Math.sin(angular) * Math.cos(bearing)
  );
  const lng2 =
    lng1 +
    Math.atan2(
      Math.sin(bearing) * Math.sin(angular) * Math.cos(lat1),
      Math.cos(angular) - Math.sin(lat1) * Math.sin(lat2)
    );

  return { lat: toDegrees(lat2), lng: toDegrees(lng2) };
}

function toRadians(degrees) {
  return (degrees * Math.PI) / 180;
}

function toDegrees(radians) {
  return (radians * 180) / Math.PI;
}

function round(value) {
  return Number(value.toFixed(COORDINATE_PRECISION));
}

function uuid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}
